import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", interest: "membership" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "", interest: "membership" });
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <div className="overline">Get in touch</div>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl leading-tight">
            Contact us, collaborate, or ask a clinical question.
          </h1>
          <p className="mt-6 text-lg text-[color:var(--azsam-ink-soft)] leading-relaxed">
            AzSAM is a volunteer-led organization of addiction specialists. A board member or committee lead
            personally reviews every message — most inquiries are answered within one business day.
          </p>

          <div className="mt-10 space-y-5">
            <InfoRow icon={Mail} label="Email" value="Leadership@AZ-SAM.org" />
          </div>

          <div className="mt-10 p-6 bg-[color:var(--azsam-cream)] rounded-2xl border border-[color:var(--azsam-stone)]">
            <div className="overline">Interested in Membership?</div>
            <p className="mt-3 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed">
              <Link to="/register" className="text-[color:var(--azsam-clay)] font-semibold hover:underline underline-offset-4" data-testid="contact-join-link">
                Join the AzSAM Community
              </Link>{" "}
              to create an account and access member events and resources.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="azsam-card p-8 lg:p-10 space-y-5" data-testid="contact-form">
          <Field label="Full name" required>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition"
              data-testid="contact-name"
              placeholder="Dr. Casey Rivera"
            />
          </Field>
          <Field label="Email" required>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition"
              data-testid="contact-email"
              placeholder="you@clinic.org"
            />
          </Field>
          <Field label="I'm reaching out about">
            <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
              <SelectTrigger className="h-12 border-[color:var(--azsam-stone)] rounded-lg" data-testid="contact-interest">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="membership">Membership inquiry</SelectItem>
                <SelectItem value="cme">CME / events</SelectItem>
                <SelectItem value="advocacy">Advocacy &amp; policy</SelectItem>
                <SelectItem value="press">Press &amp; media</SelectItem>
                <SelectItem value="general">General question</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Subject" required>
            <input
              required
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition"
              data-testid="contact-subject"
            />
          </Field>
          <Field label="Message" required>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition resize-y"
              data-testid="contact-message"
            />
          </Field>
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="contact-submit">
            {submitting ? "Sending…" : sent ? "Sent — thank you" : (<>Send message <ArrowRight size={16} /></>)}
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)] mb-2 font-semibold">
        {label}{required && <span className="text-[color:var(--azsam-clay)]"> *</span>}
      </div>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg bg-[color:var(--azsam-clay)]/10 text-[color:var(--azsam-clay)] flex items-center justify-center flex-shrink-0">
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)]">{label}</div>
        <div className="mt-1 text-[color:var(--azsam-ink)] font-medium">{value}</div>
      </div>
    </div>
  );
}
